// Options for the Attrition tab's filters in the dashboard's Filters modal.

import { normalizeLocationKey } from '../../utils/locationKey';

// ── Tenure ───────────────────────────────────────────────────────────────────
// The tenure bands the tab can be filtered by, as a FALLBACK: a copy of
// TENURE_BUCKETS (+ Unclassified) in apps/api/src/services/attrition.service.ts, used
// only until the tab's first response arrives. From then on the modal offers the
// bands that response carries (AttritionTab's onFacetsLoaded), so this copy cannot
// leave a stale band on offer for longer than the first load. The KEYS are what
// travel to the API (`tenures=0-7-days,5-8-years`); the API ignores a key it does
// not know.
export interface AttritionTenureOption {
  key: string;
  label: string;
}

export const ATTRITION_TENURE_OPTIONS: readonly AttritionTenureOption[] = [
  { key: '0-7-days', label: '0 to 7 days' },
  { key: '7-30-days', label: '7 to 30 days' },
  { key: '30-days-3-months', label: '30 days to 3 months' },
  { key: '3-6-months', label: '3 months to 6 months' },
  { key: '6m-1y', label: '6 months to 1 year' },
  { key: '1-2-years', label: '1 year to 2 years' },
  { key: '2-5-years', label: '2 years to 5 years' },
  { key: '5-8-years', label: '5 years to 8 years' },
  { key: 'over-8-years', label: '8 years and above' },
  { key: 'unclassified', label: 'Unclassified' },
];

// ── Location ─────────────────────────────────────────────────────────────────

/**
 * `base` plus every value of `extra` that is not already in it, sorted.
 *
 * The dashboard's State / City / Region options come from the stores currently
 * staffed. For attrition that list misses exactly the places attrition has emptied —
 * a city whose only store has closed, or a manager's store whose last team member
 * left — so the tab adds where its leavers were last posted.
 *
 * "Already in it" is judged on the matching key, the same one the API filters on, so
 * a leaver's "KERALA" is not added beside the store list's "Kerala". The base
 * spelling is kept. Blank values, and values with no usable key, are never added.
 */
export const mergeLocationOptions = (base: readonly string[], extra: readonly string[]): string[] => {
  const seen = new Set(base.map(normalizeLocationKey));
  const out = [...base];
  for (const value of extra) {
    const key = normalizeLocationKey(value);
    if (!value || !key || seen.has(key)) continue;
    seen.add(key);
    out.push(value);
  }
  return out.sort();
};
